import { useEffect, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Skeleton } from "@/components/ui/skeleton";
import { Wind, Gauge, ShieldAlert } from "lucide-react";

interface AqDay {
  date: string;
  usAqi: number | null;
  europeanAqi: number | null;
}

interface AqData {
  available: boolean;
  current: {
    pm25: number | null;
    pm10: number | null;
    o3: number | null;
    no2: number | null;
    so2: number | null;
    co: number | null;
    europeanAqi: number | null;
    usAqi: number | null;
  } | null;
  daily: AqDay[];
}

function aqiBand(usAqi: number | null): { label: string; color: string; bar: string } {
  if (usAqi == null) return { label: "—", color: "text-muted-foreground", bar: "bg-muted" };
  if (usAqi <= 50) return { label: "Good", color: "text-emerald-400", bar: "bg-emerald-500" };
  if (usAqi <= 100) return { label: "Moderate", color: "text-amber-300", bar: "bg-amber-500" };
  if (usAqi <= 150) return { label: "Unhealthy (Sensitive)", color: "text-orange-400", bar: "bg-orange-500" };
  if (usAqi <= 200) return { label: "Unhealthy", color: "text-red-400", bar: "bg-red-500" };
  if (usAqi <= 300) return { label: "Very Unhealthy", color: "text-purple-400", bar: "bg-purple-500" };
  return { label: "Hazardous", color: "text-rose-400", bar: "bg-rose-500" };
}

const COMPONENTS = [
  { key: "pm25" as const, label: "PM2.5", unit: "µg/m³", max: 55 },
  { key: "pm10" as const, label: "PM10", unit: "µg/m³", max: 150 },
  { key: "o3" as const, label: "O₃", unit: "µg/m³", max: 160 },
  { key: "no2" as const, label: "NO₂", unit: "µg/m³", max: 100 },
  { key: "so2" as const, label: "SO₂", unit: "µg/m³", max: 100 },
  { key: "co" as const, label: "CO", unit: "µg/m³", max: 1500 },
];

export function AirQualityPanel({ lat, lon }: { lat: number; lon: number }) {
  const [data, setData] = useState<AqData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/air-quality?lat=${lat}&lon=${lon}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [lat, lon]);

  if (loading) return <Skeleton className="h-64 rounded-2xl" />;

  const band = aqiBand(data?.current?.usAqi ?? null);

  return (
    <GlassPanel intensity="low" className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
            <Wind className="h-3 w-3" /> Air Quality
          </h3>
          <p className="text-[10px] text-muted-foreground/60 mt-0.5">Open-Meteo · components</p>
        </div>
        <span className="text-[10px] text-muted-foreground/50">US AQI</span>
      </div>

      {!data || !data.available || !data.current ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <ShieldAlert className="h-8 w-8 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">No air quality data available for this location.</p>
        </div>
      ) : (
        <>
          <div className="flex items-end gap-3 mb-5">
            <p className={`text-4xl font-bold font-mono tracking-tight ${band.color}`}>
              {data.current.usAqi ?? "—"}
            </p>
            <div className="pb-1">
              <p className={`text-sm font-semibold ${band.color}`}>{band.label}</p>
              <p className="text-[10px] text-muted-foreground">
                EU AQI {data.current.europeanAqi ?? "—"}
              </p>
            </div>
          </div>

          <div className="space-y-2.5">
            {COMPONENTS.map((c) => {
              const value = data.current![c.key];
              const pct = Math.min(100, ((value ?? 0) / c.max) * 100);
              return (
                <div key={c.key}>
                  <div className="flex items-center justify-between text-[10px] mb-1">
                    <span className="font-medium text-muted-foreground">{c.label}</span>
                    <span className="font-mono">
                      {value != null ? `${value.toFixed(value >= 100 ? 0 : 1)} ${c.unit}` : "—"}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
                    <div className={`h-full rounded-full ${band.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {data.daily.length > 0 && (
            <div className="mt-5 pt-4 border-t border-border/20">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                <Gauge className="h-3 w-3" /> 5-Day Forecast
              </p>
              <div className="grid grid-cols-5 gap-2">
                {data.daily.map((d) => {
                  const b = aqiBand(d.usAqi);
                  return (
                    <div key={d.date} className="flex flex-col items-center gap-1">
                      <span className="h-2 w-full rounded-full overflow-hidden bg-muted/60">
                        <span
                          className={`block h-full rounded-full ${b.bar}`}
                          style={{ width: `${Math.min(100, ((d.usAqi ?? 0) / 300) * 100)}%` }}
                        />
                      </span>
                      <span className={`text-[10px] font-mono ${b.color}`}>{d.usAqi ?? "—"}</span>
                      <span className="text-[9px] text-muted-foreground">
                        {new Date(d.date + "T00:00:00").toLocaleDateString([], { weekday: "short" })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </GlassPanel>
  );
}
